import { Link } from "raviger";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { Avatar } from "@/components/Common/Avatar";
import { FacilityMapsLink } from "@/components/Facility/FacilityMapLink";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TooltipComponent } from "@/components/ui/tooltip";
import { FeatureBadge } from "@/pages/Facility/Utils";
import { FacilityPublicRead, FacilityRead } from "@/types/facility/facility";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  facility: FacilityRead | FacilityPublicRead;
  className?: string;
}

export function FacilityCard({ facility, className }: Props) {
  const { t } = useTranslation();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [visibleFeatures, setVisibleFeatures] = useState<number[]>([]);
  const [showViewMore, setShowViewMore] = useState(false);

  const calculateVisibleFeatures = () => {
    const element = elementRef.current;
    if (!element) return;

    const children = Array.from(element.children) as HTMLElement[];
    const rowMap: number[][] = [];
    let currentTop = -1;
    let rowIndex = -1;

    children.forEach((child, index) => {
      const top = child.offsetTop;

      if (top !== currentTop) {
        currentTop = top;
        rowIndex++;

        rowMap[rowIndex] = [];
      }

      rowMap[rowIndex].push(facility.features[index] as number);
    });

    const totalRows = rowMap.length;

    if (totalRows <= 4) {
      setVisibleFeatures(facility.features as number[]);
      setShowViewMore(false);
      return;
    }

    const fourthRow = rowMap[3] || [];
    const result: number[] = [];

    rowMap.slice(0, 3).forEach((row) => {
      result.push(...row);
    });

    if (fourthRow.length === 1) {
      result.push(...fourthRow);
    } else {
      result.push(...fourthRow.slice(0, fourthRow.length - 1));
    }

    setVisibleFeatures(result);
    setShowViewMore(true);
  };

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    let frameId: number;

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        calculateVisibleFeatures();
      });
    });

    observer.observe(element);
    // Initial calculation
    calculateVisibleFeatures();

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [facility.features]);

  return (
    <Card className={cn("overflow-hidden bg-white dark:bg-gray-950", className)}>
      <div className="flex flex-col p-4">
        <div className="flex sm:flex-row flex-col items-center">
          <div className="flex items-center justify-center w-40 shrink-0">
            <div className="size-18 shrink-0 overflow-hidden rounded-lg">
              <Avatar
                imageUrl={facility.read_cover_image_url}
                name={facility.name || ""}
              />
            </div>
          </div>
          <div className="flex flex-col items-center sm:items-start justify-center">
            <h3 className="truncate text-xl font-semibold">{facility.name}</h3>
            <p className="text-sm text-gray-600 mt-1 dark:text-gray-400">
              {facility.facility_type}
            </p>
          </div>
        </div>
        <div className="flex sm:flex-row flex-col items-center w-full sm:h-[11rem]">
          <div className="flex flex-col items-center gap-2 sm:w-40 h-full shrink-0 pt-2">
            <p className="flex flex-col grow items-center text-center text-sm text-gray-500">
              <TooltipComponent
                side="right"
                content={[facility.address].filter(Boolean).join(", ")}
                className="max-w-sm"
              >
                <span className="line-clamp-1 pr-3">
                  {[facility.address].filter(Boolean).join(", ")}
                </span>
              </TooltipComponent>
              <span>
                {facility.geo_organization.parent?.name},{" "}
                {facility.geo_organization.parent?.parent?.name},{" "}
                {facility.pincode}
              </span>
              {facility.latitude && facility.longitude && (
                <FacilityMapsLink
                  latitude={facility.latitude}
                  longitude={facility.longitude}
                />
              )}
            </p>
            <Button variant="primary" asChild>
              <Link href={`/facility/${facility.id}`}>
                {t("view_facility")}
              </Link>
            </Button>
          </div>
          <div className="hidden sm:flex flex-col gap-y-2 h-full w-full bg-gray-50 border border-gray-50 rounded-md p-3">
            <h5 className="text-sm font-semibold">{t("our_services")}</h5>
            <div className="relative w-full">
              <div
                ref={elementRef}
                className="absolute invisible pointer-events-none flex flex-wrap items-start gap-2"
              >
                {facility.features?.map((featureId) => (
                  <FeatureBadge
                    key={featureId}
                    featureId={featureId as number}
                  />
                ))}
              </div>
              <div className="flex flex-wrap items-start gap-2 max-h-[7.5rem] overflow-hidden">
                {visibleFeatures.map((featureId) => (
                  <FeatureBadge
                    key={featureId}
                    featureId={featureId as number}
                  />
                ))}
                {showViewMore && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="link" size="xs" className="px-0">
                        {t("view_more")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent side="top" className="w-[30rem]">
                      <div className="flex flex-col gap-y-3 w-full bg-gray-50 border border-gray-50 rounded-md p-3">
                        <h5 className="text-sm font-semibold">
                          {t("our_services")}
                        </h5>
                        <div className="flex flex-wrap items-start gap-2">
                          {facility.features?.map((featureId) => (
                            <FeatureBadge
                              key={featureId}
                              featureId={featureId as number}
                            />
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          </div>
        </div>
        <Accordion type="single" collapsible className="w-full sm:hidden pt-4">
          <AccordionItem
            className="flex flex-col gap-y-3 bg-gray-50 shadow rounded-md px-3"
            value={"our-services"}
          >
            <AccordionTrigger className="flex justify-between">
              <h5 className="text-sm font-semibold">{t("our_services")}</h5>
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-wrap items-start gap-2">
                {facility.features?.map((featureId) => (
                  <FeatureBadge
                    key={featureId}
                    featureId={featureId as number}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </Card>
  );
}
